import { environment } from "../../environments/environment";

export interface CellMapping {
  apiBase: string;
  tmBase: string;
  helpBase: string;
}

const LOCAL_PATTERNS = [/^localhost$/i, /\.localhost$/i, /\.local$/i, /\.test$/i, /\.localdomain$/i];

interface Rule {
  pattern: RegExp;
  derive: (match: RegExpMatchArray, protocol: string) => CellMapping;
}

const RULES: Rule[] = [
  // WCX prod US: TM lives on nice-wfm.com (intentional). Must run before the IEX rule.
  {
    pattern: /^use1\.nice-esp\.com$/i,
    derive: (_m, p) => ({
      apiBase: `${p}//api.use1.nice-esp.com`,
      tmBase: `${p}//api.use1.nice-wfm.com`,
      helpBase: `${p}//help.api.use1.nice-esp.com`,
    }),
  },
  // IEX UK Sovereign (prod-uk2-esp): the served domain carries no region, and the
  // service hosts append it with a hyphen (tm-api-uk2.) rather than a dot label.
  {
    pattern: /^wem-esp\.nicecxone-sov1\.uk$/i,
    derive: (_m, p) => ({
      apiBase: `${p}//api-uk2.wem-esp.nicecxone-sov1.uk`,
      tmBase: `${p}//tm-api-uk2.wem-esp.nicecxone-sov1.uk`,
      helpBase: `${p}//help-api-uk2.wem-esp.nicecxone-sov1.uk`,
    }),
  },
  // WCX dev letter-suffixed envs (devA, devB, ...) share dev1's backend.
  // Must run before the generic nicewfmdev family rule.
  {
    pattern: /^dev[a-z]\.esp\.nicewfmdev\.com$/i,
    derive: (_m, p) => ({
      apiBase: `${p}//api.dev1.esp.nicewfmdev.com`,
      tmBase: `${p}//api.dev1.saas.nicewfmdev.com`,
      helpBase: `${p}//help.api.dev1.saas.nicewfmdev.com`,
    }),
  },
  // WCX nicewfmdev family (dev1, tst1, ...).
  {
    pattern: /^([^.]+)\.esp\.nicewfmdev\.com$/i,
    derive: (m, p) => ({
      apiBase: `${p}//api.${m[1]}.esp.nicewfmdev.com`,
      tmBase: `${p}//api.${m[1]}.saas.nicewfmdev.com`,
      helpBase: `${p}//help.api.${m[1]}.saas.nicewfmdev.com`,
    }),
  },
  // WCX nicewfm-stg family (staging).
  {
    pattern: /^esp\.([^.]+)\.nicewfm-stg\.com$/i,
    derive: (m, p) => ({
      apiBase: `${p}//api.esp.${m[1]}.nicewfm-stg.com`,
      tmBase: `${p}//api.saas.${m[1]}.nicewfm-stg.com`,
      helpBase: `${p}//help.api.saas.${m[1]}.nicewfm-stg.com`,
    }),
  },
  // IEX nice-esp.com family (uk1, na1, future regions).
  {
    pattern: /^([^.]+)\.nice-esp\.com$/i,
    derive: (m, p) => ({
      apiBase: `${p}//api.${m[1]}.nice-esp.com`,
      tmBase: `${p}//tm-api.${m[1]}.nice-esp.com`,
      helpBase: `${p}//help.api.${m[1]}.nice-esp.com`,
    }),
  },
  // CXone family. Group 1 is the cell (must end in digits, e.g. na1); group 2 is
  // an optional env infix (letters only, e.g. dev/tst). The cell pattern rules out
  // tenant subdomains for CXone prod (which would otherwise be captured as the cell).
  // CXone has no separate Help URL (helpBase is empty).
  // Note: the app is served from nice-incontact.com (hyphenated), but the API/TM
  // services live on niceincontact.com (no hyphen) — so the match pattern keeps the
  // hyphen while the derived bases drop it.
  {
    pattern: /^([a-z]+\d+)(?:\.([a-z]+))?\.nice-incontact\.com$/i,
    derive: (m, p) => {
      const cell = m[1];
      const envInfix = m[2] ? `.${m[2]}` : "";
      return {
        apiBase: `${p}//api-${cell}${envInfix}.niceincontact.com/esp`,
        tmBase: `${p}//api-${cell}${envInfix}.niceincontact.com/esp/tm`,
        helpBase: "",
      };
    },
  },
];

function tryRules(host: string, protocol: string): CellMapping | null {
  for (const rule of RULES) {
    const match = host.match(rule.pattern);
    if (match) {
      return rule.derive(match, protocol);
    }
  }
  return null;
}

export function resolve(hostname: string, protocol: string): CellMapping {
  if (LOCAL_PATTERNS.some(p => p.test(hostname))) {
    return {
      apiBase: environment.espEndpointBaseUrl,
      tmBase: environment.saasEndpointBaseUrl,
      helpBase: environment.espHelpUrl,
    };
  }

  const fullMatch = tryRules(hostname, protocol);
  if (fullMatch) {
    return fullMatch;
  }

  const labels = hostname.split(".");
  const suffix = labels.length >= 2 ? labels.slice(1).join(".") : hostname;
  const strippedMatch = tryRules(suffix, protocol);
  if (strippedMatch) {
    return strippedMatch;
  }

  return {
    apiBase: `${protocol}//api.${suffix}`,
    tmBase: `${protocol}//tm-api.${suffix}`,
    helpBase: `${protocol}//help.api.${suffix}`,
  };
}

let cached: CellMapping | null = null;

function current(): CellMapping {
  if (!cached) {
    cached = resolve(window.location.hostname, window.location.protocol);
  }
  return cached;
}

export function apiUrl(path: string): string {
  return current().apiBase + path;
}

export function tmUrl(path: string): string {
  return current().tmBase + path;
}

export function helpUrl(path: string): string {
  return current().helpBase + path;
}

export function hasHelp(): boolean {
  return current().helpBase !== "";
}

export function __resetCacheForTests(): void {
  cached = null;
}
